import { Handshake, ShieldCheck, GlobeLock } from 'lucide-react';
import { useAuth0 } from '@auth0/auth0-react';
import { useSearch } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { testId } from 'tests/e2e/utils/test-ids.constant';
import { AuthOptions, Dimensions } from 'src/shared/lib/auth.utils';
import { SsoMethodButtons } from 'src/features/auth/components/SsoMethodButtons';
import { Card } from 'src/shared/components/ui/card';
import { Separator } from 'src/shared/components/ui/separator';
import { Avatar, AvatarImage } from 'src/shared/components/ui/icons/avatar';
import { Button } from 'src/shared/components/ui/buttons/button';
import InputWithLabel from 'src/shared/components/ui/inputs/input-with-label';
import { useHostname } from 'src/shared/contexts/HostnameContext';

type LoginSignUpCardProps = {
  variant: 'login' | 'signup';
  logo: string | null | undefined;
  dimensions: Dimensions | undefined;
  authOptions?: AuthOptions | null;
};

function LoginSignUpCard({ variant, logo, dimensions, authOptions }: LoginSignUpCardProps) {
  const { loginWithRedirect } = useAuth0();
  const [email, setEmail] = useState('');
  const { hostname } = useHostname();
  const name = hostname.split('.')[0];

  const authMethods = authOptions?.authMethods || [];
  const orgId = authOptions?.organisationId;

  const hasSSO = authMethods.some((method) => method.type === 'SSO');
  const isAppTekst = hostname === 'app.tekst';

  const search = useSearch({ strict: false }) as Record<string, string | undefined>;
  const next = search.next;
  const hint = search.hint;

  useEffect(() => {
    if (hint) {
      setEmail(hint);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function login(e?: React.FormEvent, product?: string) {
    e?.preventDefault();

    const connection = product ? 'SSO' : 'Username-Password-Authentication';
    const resource = product ? `tenant=${orgId}&product=${product}` : undefined;

    await loginWithRedirect({
      authorizationParams: {
        prompt: 'login',
        login_hint: email,
        redirect_uri: `${window.location.origin}?next=${next}`,
        connection: connection,
        ...(connection === 'SSO' ? { resource } : {}),
      },
    });
  }

  async function ssoLogin(e: React.MouseEvent<HTMLButtonElement>, product: string) {
    e.preventDefault();
    await login(undefined, product);
  }

  async function signUp(e?: React.FormEvent) {
    e?.preventDefault();
    await loginWithRedirect({
      authorizationParams: {
        screen_hint: 'signup',
        login_hint: email,
        connection: 'Username-Password-Authentication',
        redirect_uri: `${window.location.origin}?next=${next}`,
      },
    });
  }

  return (
    <div className="w-full h-full overflow-y-auto scrollbar-hide">
      <div className="min-h-full flex flex-col items-center justify-center py-4">
        <div className="h-fit flex flex-col items-center justify-center">
          <div className="flex items-center shrink-0 gap-4 mx-auto justify-center mb-4 pr-3 h-[20px]">
            <img
              src="/static/images/tekst-logo.svg"
              width={100}
              height={100}
              alt="Tekst logo"
              className="w-auto h-[18px]"
            />
            {!isAppTekst && (logo || name) && (
              <>
                <Separator orientation="vertical" className="h-[20px] bg-neutral-500" />
                {logo ? (
                  <>
                    {dimensions && dimensions.width / dimensions.height < 1.5 ? (
                      <div className="flex items-center gap-2">
                        <Avatar className="h-[24px] w-[24px]">
                          <AvatarImage className="object-contain" src={logo} />
                        </Avatar>
                        {name && (
                          <p className="text-lg font-medium text-neutral-700 leading-none">
                            {decodeURIComponent(name)}
                          </p>
                        )}
                      </div>
                    ) : (
                      <img src={logo} width={100} height={100} alt="Company logo" className="w-auto h-[18px]" />
                    )}
                  </>
                ) : (
                  name && (
                    <p className="text-lg font-medium text-neutral-700 leading-none">{decodeURIComponent(name)}</p>
                  )
                )}
              </>
            )}
          </div>
          <Card className="w-[360px] px-7 pt-6 pb-6">
            <div className="mb-4 w-full text-center">
              <p className="text-neutral-400">{variant === 'login' ? 'Login' : 'Sign Up'}</p>
            </div>
            <form onSubmit={variant === 'login' ? login : signUp} className="flex flex-col gap-1 w-full">
              <InputWithLabel
                id="email"
                autoFocus
                label="Email"
                value={email}
                className="p-0"
                placeholder="Your email"
                disable1Password={false}
                onChange={(e) => setEmail(e.target.value)}
                data-testid={variant === 'login' ? testId.login.emailInput : testId.signup.emailInput}
              />
              <Button
                type="submit"
                data-testid={variant === 'login' ? testId.login.continueButton : testId.signup.continueButton}
              >
                {variant === 'login' ? 'Continue' : 'Sign Up'}
              </Button>
              {hasSSO && (
                <div className="w-full flex items-center justify-center my-2">
                  <div className="w-full h-[1px] bg-neutral-200" />
                  <span className="text-neutral-400 text-xs px-2 uppercase">SSO</span>
                  <div className="w-full h-[1px] bg-neutral-200" />
                </div>
              )}
              <SsoMethodButtons
                methods={authMethods}
                getLabel={(labelName) => `${variant === 'login' ? 'Login with' : 'Sign up with'} ${labelName}`}
                onClick={(e, method) => ssoLogin(e, method.id)}
              />
              <div className="w-full flex items-center justify-center mt-4">
                <p className="text-sm text-neutral-400">
                  {variant === 'login' ? 'No account yet?' : 'Already have an account?'}
                  {variant === 'login' ? (
                    <a
                      href="https://www.tekst.com/talk-to-tekst?ref=login"
                      target="_blank"
                      rel="noreferrer"
                      className="hover:text-primary underline underline-offset-1 cursor-pointer transition-colors ml-1"
                    >
                      Book a demo
                    </a>
                  ) : (
                    <span
                      onClick={() => login()}
                      className="hover:text-primary underline underline-offset-1 cursor-pointer transition-colors ml-1"
                    >
                      Login
                    </span>
                  )}
                </p>
              </div>
            </form>
            <div className="mx-auto flex items-center justify-evenly gap-3 mt-6">
              <a target="_blank" href="https://trust.tekst.com/" rel="noreferrer">
                <div className="text-neutral-400 text-xs flex flex-col  items-center gap-1 cursor-pointer hover:underline">
                  <GlobeLock size={14} />
                  SOC 2
                </div>
              </a>
              <a
                target="_blank"
                href="https://assets-global.website-files.com/6560a9f62d4104b1c8efc149/659eb9956e73f13d3dfef210_Tekst.ai%20%20-%20ISO%20IEC%2027001%202022%20Official%20Certificate%20(1).pdf"
                rel="noreferrer"
              >
                <div className="text-neutral-400 text-xs flex flex-col items-center gap-1 cursor-pointer hover:underline">
                  <ShieldCheck size={14} />
                  ISO 27001
                </div>
              </a>
              <a target="_blank" href="https://trust.tekst.com/" rel="noreferrer">
                <div className="text-neutral-400 text-xs flex flex-col  items-center gap-1 cursor-pointer hover:underline">
                  <Handshake size={14} />
                  GDPR
                </div>
              </a>
            </div>
          </Card>
          <div className="mx-auto text-neutral-400 text-xs shrink-0 flex-center h-8 hover:underline">
            <a target="_blank" href="https://www.tekst.com/legal/privacy-policy" rel="noreferrer">
              Privacy Policy
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

export default LoginSignUpCard;
